import { useEffect, useMemo, useState } from 'react'
import { ConnectError } from '@connectrpc/connect'
import {
  createConnectQueryKey,
  useMutation as useConnectMutation,
} from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { authTransport } from '@jotta/grpc-connect-client/transport'
import {
  changePayment as changePaymentDescriptor,
  createStripeIntent as createStripeIntentDescriptor,
  createVippsIntent as createVippsIntentDescriptor,
  getPaymentInfo as getPaymentInfoDescriptor,
} from '@jotta/grpc-connect-openapi/subscriptionQuery'
import { ErrorType } from '@jotta/grpc-web/no/jotta/openapi/error_pb'
import { navigate, useAndConsumeSearchParam } from '@jotta/router'
import { handleGrpcError } from '@jotta/types/AppError'
import { cn } from '@jotta/utils/css'
import { redirectAndWait } from '@jotta/utils/redirect'

import { RadioInput } from '../../Forms/RadioInput/RadioInput'
import SvgMastercard from '../../generated/icons/SvgMastercard'
import SvgPaypal from '../../generated/icons/SvgPaypal'
import SvgVippsText from '../../generated/icons/SvgVippsText'
import SvgVisa from '../../generated/icons/SvgVisa'
import { PlainBrandIcon } from '../../Icons/BrandIcon/BrandIcon'

import { PaymentMethodOption } from './paymentMethodOptions'
import type { StripeValidationError } from './Stripe'

const paypalClientSecretParamKey = 'paypal-client-secret'

export function usePaymentMethodMutation({
  onSuccess,
  onOpen,
}: {
  onSuccess: () => void
  onOpen?: () => void
}) {
  const elements = useElements()
  const stripe = useStripe()
  const [error, setError] = useState<string | undefined>()
  const clearError = () => setError(undefined)
  const [stripeError, setStripeError] = useState<StripeValidationError>({})
  const [paymentMethod, setPaymentMethod] = useState<
    PaymentMethodOption | undefined
  >()
  const clientSecretParam = useAndConsumeSearchParam(paypalClientSecretParamKey)
  const queryClient = useQueryClient()
  const { mutateAsync: changePayment } = useConnectMutation(
    changePaymentDescriptor,
    {
      transport: authTransport,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getPaymentInfoDescriptor,
            input: {},
            transport: authTransport,
            cardinality: 'finite',
          }),
        })
      },
    },
  )
  const { mutateAsync: createStripeIntent } = useConnectMutation(
    createStripeIntentDescriptor,
    {
      throwOnError: false,
      transport: authTransport,
    },
  )
  const { mutateAsync: createVippsIntent } = useConnectMutation(
    createVippsIntentDescriptor,
    {
      transport: authTransport,
    },
  )

  const tryCreateStripeIntent = async (
    paymentMethodId: string,
  ): Promise<
    { ok: true; clientSecret: string } | { ok: false; error: string }
  > => {
    try {
      const { clientSecret } = await createStripeIntent({
        paymentMethod: paymentMethodId,
      })
      return { ok: true, clientSecret }
    } catch (e) {
      const err =
        e instanceof ConnectError ? handleGrpcError(e.rawMessage) : undefined

      let errMsg =
        err && err.errors && err.errors[0]?.code === ErrorType.PAYMENT_DECLINED
          ? t`Changing payment failed. Your card was declined. Please try again or use another payment method.`
          : t`Changing payment failed. Please try again or use another payment method.`

      if (err?.xid) {
        errMsg += ` (xid: ${err.xid})`
      }

      return {
        ok: false,
        error: errMsg,
      }
    }
  }

  const { mutate: verifyAndSetPaypal, isPending: pendingPayPal } = useMutation({
    throwOnError: false,
    mutationFn: async (clientSecret: string) => {
      setPaymentMethod(PaymentMethodOption.STRIPE_PAYPAL)
      navigate(
        {
          pathname: location.pathname,
          search: '',
        },
        { replace: true },
      )
      if (!stripe) {
        return
      }
      onOpen?.()
      const { error, setupIntent } =
        await stripe.retrieveSetupIntent(clientSecret)
      if (
        error ||
        setupIntent.status !== 'succeeded' ||
        !setupIntent.payment_method
      ) {
        onOpen?.()
        setError(
          t`Changing payment failed. Please try again or use another payment method`,
        )
        return
      }
      const paymentIntentId =
        typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method.id

      await changePayment({ paymentMethod: paymentIntentId })
      clearError()
      onSuccess()
    },
  })

  useEffect(() => {
    if (clientSecretParam) {
      verifyAndSetPaypal(clientSecretParam)
    }
  }, [clientSecretParam, stripe])

  const { mutate: onChangePaymentMethod, isPending } = useMutation({
    throwOnError: false,
    mutationFn: async () => {
      if (paymentMethod === PaymentMethodOption.VIPPS) {
        const { confirmationUrl } = await createVippsIntent({
          redirectUrl: window.location.toString(),
        })
        await redirectAndWait(confirmationUrl)
        return
      }
      if (!stripe || !elements) {
        setError(
          t`Changing payment failed. Please try again or use another payment method.`,
        )
        return
      }
      const cardNumberElement = elements.getElement(CardNumberElement)
      if (
        paymentMethod === PaymentMethodOption.STRIPE_CARD &&
        cardNumberElement
      ) {
        const { error: createError, paymentMethod: stripePaymentMethod } =
          await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumberElement,
          })
        if (createError) {
          setError(createError?.message)
          return
        }
        const intentResult = await tryCreateStripeIntent(stripePaymentMethod.id)
        if (!intentResult.ok) {
          setError(intentResult.error)
          return
        }

        const { error: confirmError } = await stripe.confirmCardSetup(
          intentResult.clientSecret,
        )
        if (confirmError) {
          setError(confirmError?.message)
          return
        }
        await changePayment({
          paymentMethod: stripePaymentMethod.id,
        })
        clearError()
        onSuccess()
      } else if (paymentMethod === PaymentMethodOption.STRIPE_PAYPAL) {
        const { error: createError, paymentMethod: stripePaymentMethod } =
          await stripe.createPaymentMethod({
            type: 'paypal',
          })
        if (createError) {
          setError(createError?.message)
          return
        }
        const intentResult = await tryCreateStripeIntent(stripePaymentMethod.id)
        if (!intentResult.ok) {
          setError(intentResult.error)
          return
        }
        const { clientSecret } = intentResult

        const returnURL = new URL(
          window.location.origin + window.location.pathname,
        )
        returnURL.searchParams.set(paypalClientSecretParamKey, clientSecret)
        const { error: confirmError } = await stripe.confirmPayPalSetup(
          clientSecret,
          {
            return_url: returnURL.href,
          },
        )
        if (confirmError) {
          setError(confirmError?.message)
        }
      }
    },
  })

  return {
    paymentMethod,
    setPaymentMethod,
    error,
    clearError,
    stripeError,
    setStripeError,
    onChangePaymentMethod,
    isPending,
    pendingPayPal,
    elements,
  }
}

export function PaymentRadio({
  paymentMethods,
  paymentMethod,
  setPaymentMethod,
}: {
  paymentMethods: PaymentMethodOption[] | undefined
  paymentMethod: PaymentMethodOption | undefined
  setPaymentMethod: (method: PaymentMethodOption) => void
}) {
  const pms = useMemo(
    () =>
      paymentMethods?.map(pm => {
        switch (pm) {
          case PaymentMethodOption.STRIPE_CARD:
            return {
              name: t`Payment card`,
              icons: [SvgVisa, SvgMastercard],
              pm: pm,
            }
          case PaymentMethodOption.STRIPE_PAYPAL:
            return {
              name: 'PayPal',
              icons: [SvgPaypal],
              pm: pm,
            }
          case PaymentMethodOption.VIPPS:
            return {
              name: 'Vipps',
              icons: [SvgVippsText],
              pm: pm,
            }
        }
      }),
    [paymentMethods],
  )

  useEffect(() => {
    if (pms && !paymentMethod) {
      const firstMethod = pms.at(0)
      if (firstMethod !== undefined) {
        setPaymentMethod(firstMethod.pm)
      }
    }
  }, [pms, paymentMethod])

  return (
    <div
      className="flex flex-col gap-2 sm:flex-row"
      aria-label={t`Choose a payment method`}
    >
      {pms?.map(pm => {
        const checked = pm.pm === paymentMethod
        return (
          <label
            className={cn(
              'grid flex-1 grid-cols-[auto_20px] gap-x-2 rounded-md bg-surface-container-lowest border border-b px-2 py-4 shadow-sm hover:cursor-pointer',
              // { 'bg-btn-outline-hover': checked },
            )}
            aria-label={pm.name}
            key={pm.pm}
          >
            <div className="flex justify-center self-center">
              {pm.icons.map((icon, index) => (
                <PlainBrandIcon key={index} icon={icon} height={20} />
              ))}
            </div>
            <RadioInput
              className="radio flex-wrap self-center justify-self-center"
              checked={checked}
              onChange={() => {
                setPaymentMethod(pm.pm)
              }}
              name="payment-methods"
              value={pm.name}
              aria-label={pm.name}
            />
          </label>
        )
      })}
    </div>
  )
}
